/**
 * Leaderboard — V1 scaffold. Global "top drivers" board. Shows a friendly
 * empty state until enough rides are completed (the board fills itself as the
 * community grows; per-route + monthly boards are V1.1).
 */
import { useQuery } from '@tanstack/react-query';
import { getLeaderboard } from '../lib/api';
import { useRouter } from '../lib/router';
import { useT } from '../lib/i18n';
import { BackButton } from '../lib/BackButton';
import { Card, CardHint } from '../components/Card';
import { Badge } from '../components/Badge';
import { RetryCard } from '../components/RetryCard';

export function LeaderboardPage(): JSX.Element {
  const { navigate } = useRouter();
  const { t } = useT();
  const q = useQuery({ queryKey: ['leaderboard'], queryFn: getLeaderboard });

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col px-4 py-6">
      <BackButton />
      <header className="mb-4">
        <h1 className="text-2xl font-bold text-tg-text">{t('leaderboard.title')}</h1>
        <p className="text-sm text-tg-hint">{t('leaderboard.subtitle')}</p>
      </header>

      {q.isLoading && (
        <div className="space-y-2">
          <div className="h-16 animate-pulse rounded-card bg-tg-bg-secondary" />
          <div className="h-16 animate-pulse rounded-card bg-tg-bg-secondary" />
          <div className="h-16 animate-pulse rounded-card bg-tg-bg-secondary" />
        </div>
      )}

      {q.isError && (
        <RetryCard
          message={(q.error as Error).message}
          onRetry={() => q.refetch()}
          isRetrying={q.isFetching}
        />
      )}

      {q.data && q.data.items.length === 0 && (
        <Card className="space-y-2 text-center">
          <p className="text-4xl">🏁</p>
          <p className="font-semibold text-tg-text">{t('leaderboard.empty.title')}</p>
          <CardHint>{t('leaderboard.empty.body')}</CardHint>
        </Card>
      )}

      {q.data && q.data.items.length > 0 && (
        <ol className="space-y-2">
          {q.data.items.map((e) => (
            <li key={e.userId}>
              <button
                type="button"
                onClick={() => navigate(`/profile/${e.userId}`)}
                className="animate-card-in flex w-full items-center gap-3 rounded-card bg-tg-bg-secondary p-3 text-left"
              >
                <span className="w-6 flex-none text-center font-bold text-brand-navy">
                  {e.rank}
                </span>
                {e.avatarUrl ? (
                  <img src={e.avatarUrl} alt="" className="h-10 w-10 rounded-full" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-navy text-brand-cream">
                    {e.displayName.slice(0, 1)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-tg-text">{e.displayName}</p>
                  <p className="text-xs text-tg-hint">
                    {t('leaderboard.rides', { n: e.ridesCompleted })}
                    {e.ratingAvg !== null && ` · ★ ${e.ratingAvg.toFixed(1)}`}
                  </p>
                </div>
                <Badge tone="brand">{t(e.tierLabelKey as 'tier.newcomer')}</Badge>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
